<template>
    <div :style="cssBinds.div">
        <v-row dense>
            <v-col cols="12" :style="cssBinds.col">
                {{ "[ " + props.item.number + " ] " + props.item.role.name }}
            </v-col>
        </v-row>
        <v-row dense>
            <v-col cols="12" :style="cssBinds.col">
                <VueApexCharts
                    height="400px"
                    :options="voltageChart.options"
                    :series="voltageChart.chartData"
                    :style="cssBinds.chart"
                />
            </v-col>
        </v-row>
        <v-divider
            thickness="1"
            opacity="100"
            class="mt-0 pa-0"
            :style="cssBinds.divider"
        />
    </div>
</template>

<script setup>
import { reactive, onMounted, onUpdated } from "vue";
import VueApexCharts from "vue3-apexcharts";
import { FIRE_PORT_ROLE, faLog } from "../common/faCommon.js";

const FN = "[ChartVoltage] ";

const props = defineProps({
    uiMode: { type: String, required: true },
    item: { type: Object, required: true },
});

const cssBinds = reactive({
    div: {},
    row: {},
    col: {},
    chart: {},
    divider: {},
});

const getLineStrokes = (role) => {
    faLog(0, FN + "[getLineStrokes] role : " + role);
    const curve = "smooth";
    const dash = 3;
    switch (role) {
        case FIRE_PORT_ROLE.FA_PR_FIRE_DETECTION:
            return {
                curve: curve,
                width: [2, 2, 2, 2, 2, 2],
                dashArray: [0, dash, dash, dash, dash, dash],
            };
        case FIRE_PORT_ROLE.FA_PR_FIRE_EQUIPMENT:
            return {
                curve: curve,
                width: [2, 2, 2, 2, 2],
                dashArray: [0, dash, dash, dash, dash],
            };
        case FIRE_PORT_ROLE.FA_PR_EQUIPMENT_WORK:
            return {
                curve: curve,
                width: [2, 2, 2, 2, 2],
                dashArray: [0, dash, dash, dash, dash],
            };
        case FIRE_PORT_ROLE.FA_PR_DISABLED:
        default:
            return {
                curve: curve,
                width: [2, 2],
                dashArray: [0, 0],
            };
    }
};

const getLineColors = (role) => {
    faLog(0, FN + "[getLineColors] role : " + role);
    switch (role) {
        case FIRE_PORT_ROLE.FA_PR_FIRE_DETECTION:
            return [
                "#FF00FF",
                "#C8C8C8",
                "#FF1B1B",
                "#FFA51F",
                "#00B200",
                "#1990FF",
                "#0107FF",
            ];
        case FIRE_PORT_ROLE.FA_PR_FIRE_EQUIPMENT:
            return [
                "#FF00FF",
                "#FF1B1B",
                "#FFA51F",
                "#00B200",
                "#1990FF",
                "#0107FF",
            ];
        case FIRE_PORT_ROLE.FA_PR_EQUIPMENT_WORK:
            return [
                "#FF00FF",
                "#FF1B1B",
                "#FFA51F",
                "#00B200",
                "#1990FF",
                "#0107FF",
            ];
        case FIRE_PORT_ROLE.FA_PR_DISABLED:
        default:
            return props.uiMode === "light" ? ["#FF00FF", "#FFFFFFFF"] : ["#FF00FF", "#FF000000"];
    }
};

const voltageChart = reactive({
    options: {},
    chartData: [
        {
            name: "장비 전압",
            type: "line",
            data: [],
        },
    ],
});

onUpdated(() => {
    faLog(0, FN + "[onUpdated]");

    voltageChart.chartData.splice(0, voltageChart.chartData.length);
    const voltageList = [];
    const labels = [];
    const lenVoltages = props.item.voltages.length;

    const lineType = "line";

    for (let m = 0; m < lenVoltages; m++) {
        voltageList.push(props.item.voltages[m].value);
        const dt = props.item.voltages[m].dt;
        voltageChart.options.xaxis.categories.push(dt);
        voltageChart.options.xaxis.overwriteCategories.push(m + 1);
    }

    voltageChart.chartData.push({
        name: "장비 전압",
        type: lineType,
        data: voltageList,
    });

    // faLog(2, FN + "[onUpdated]", voltageChart);

    if (
        props.item.role.value !== FIRE_PORT_ROLE.FA_PR_FIRE_DETECTION &&
        props.item.role.value !== FIRE_PORT_ROLE.FA_PR_FIRE_EQUIPMENT &&
        props.item.role.value !== FIRE_PORT_ROLE.FA_PR_EQUIPMENT_WORK
    ) {
        voltageChart.chartData.push({
            name: "",
            type: lineType,
            data: [],
        });
        return;
    }

    for (let m = 0; m < props.item.ranges.length; m++) {
        const ranges = [];
        const value = props.item.ranges[m].value;
        for (let n = 0; n < lenVoltages; n++) {
            ranges.push(value);
        }
        voltageChart.chartData.push({
            name: props.item.ranges[m].name,
            type: "line",
            data: ranges,
        });
    }
});

onMounted(() => {
    faLog(0, FN + "[onMounted] props.uiMode : " + props.uiMode);
    // faLog(2, FN + "[onMounted] props", props);

    cssBinds.div = {
        // height: "calc(100% - 10px)",
        width: "calc(100% - 10px)",
        overflowY: "hidden",
        overflowX: "hidden",
        marginLeft: "5px",
        marginRight: "5px",
        marginTop: "5px",
        marginBottom: "5px",
    };

    cssBinds.col = {
        textAlign: "left",
        color: props.uiMode === "light" ? "#304030" : "#cccccc",
        fontSize: "12px",
        fontWeight: "500",
        marginLeft: "0px",
        marginRight: "0px",
        marginTop: "0px",
        marginBottom: "0px",
    };
    cssBinds.chart = {
        width: "calc(100vw - 20px)",
    };
    cssBinds.divider = props.uiMode === "light" ? { color: "#cccccc" } : { color: "#505050" };

    voltageChart.options = {
        chart: {
            id: "voltageChart",
            toolbar: {
                show: false,
                offsetX: 0,
                offsetY: 0
            },
            background:
                props.uiMode !== "light"
                    ? { color: "white" }
                    : { color: "black" },
            type: "line",
            stacked: false,
            noData: { text: "Loading..." },
        },
        legend: {
            position: "bottom",
            horizontalAlign: "left",
            offsetX: 0,
            itemMargin: { horizontal: 5, vertical: 0 },
            fontSize: "12px",
            markers: {
                strokeWidth: 0,
            },
            labels: {
                colors: props.uiMode === "light" ? "black" : "white",
                useSeriesColors: false,
            },
        },
        colors: getLineColors(props.item.role.value),
        dataLabels: {
            enabled: false
        },
        stroke: getLineStrokes(props.item.role.value),
        labels: {
            style: {
                fontSize: "10px",
                fontWeight: "normal",
                color: props.uiMode === "light" ? "black" : "white",
            },
        },
        title: {
            text: props.item.name,
            align: "left",
            offsetX: 0,
            style: {
                fontSize: "16px",
                fontWeight: "bold",
                color: props.uiMode === "light" ? "black" : "white",
            },
        },
        xaxis: {
            // axixTick: true,
            // tickPlacement: "on",
            tickAmount: 5,
            offsetX: 0,
            // convertedCatToNumeric: false,
            overwriteCategories: [],
            categories: [],
            axisBorder: {
                show: true,
                color: props.uiMode === "light" ? "#505050" : "#cccccc",
            },
            labels: {
                // offsetY: 10,
                style: {
                    fontSize: "8px",
                    fontWeight: "normal",
                    colors: props.uiMode === "light" ? "black" : "white",
                },
            },
        },
        yaxis: [
            {
                axisTicks: {
                    show: true,
                },
                axisBorder: {
                    offsetX: 0,
                    show: true,
                    color: props.uiMode === "light" ? "#505050" : "#cccccc",
                },
                labels: {
                    show: true,
                    offsetX: 0,
                    offsetY: 0,
                    style: {
                        colors: props.uiMode === "light" ? "black" : "white",
                    },
                },
                title: {
                    text: "전압 (V)",
                    style: {
                        color: props.uiMode === "light" ? "black" : "white",
                        padding: "0px",
                    },
                },
                min: 0,
                max: 35,
            },
        ],
        tooltip: {
            enabled: true,
            theme: props.uiMode,
        },
        grid: {
            padding: {
                left: 15,
                right: 10,
            },
        },
    };
});
</script>
